import {
  ArrowLeftIcon,
  BellRingingIcon,
  CaretUpIcon,
  CheckIcon,
  ClockCountdownIcon,
  EyeIcon,
  PhoneIcon,
  TrashIcon,
  UserSoundIcon,
} from '@phosphor-icons/react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useApproveChecklist } from '@/api/checklists/approve-checklist'
import {
  ChecklistBulkStatus,
  useBulkUpdateStatusChecklists,
} from '@/api/checklists/bulk-update-status-checklists'
import { useDeleteChecklist } from '@/api/checklists/delete-checklist'
import { useGetChecklistTaskDetails } from '@/api/checklists/get-checklist-task-details'
import { useSendReminderChecklist } from '@/api/checklists/send-reminder-checklist'
import { InfoRow } from '@/components/layout/info-row'
import { PageHeader } from '@/components/layout/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { DropdownMenu } from '@/components/ui/dropdown-menu'
import { EmptyState } from '@/components/ui/empty-state'
import { Icon } from '@/components/ui/icon'
import { Link } from '@/components/ui/link'
import { Table } from '@/components/ui/table'
import { popupFactory, routerFactory } from '@/factories'
import { getAssetLink } from '@/helpers/get-asset-link'
import { formatDateLong, formatDateOnly } from '@/utils/date'

import {
  getChecklistBulkStatusAction,
  getChecklistBulkStatuses,
  getChecklistStatusColor,
  getChecklistStatusLabel,
  getChecklistTypeColor,
  getChecklistTypeLabel,
} from '../checklists-dashboard/checklists-dashboard.page.utils'
import { ChecklistInputFormRow } from './checklist-input-form-row'
import { getChecklistTaskColumns } from './checklists-task.page.constants'
import * as S from './checklists-task.page.styled'
import {
  ChecklistAttachmentRow,
  ChecklistInstructionsRow,
  ChecklistSignaturesRow,
  ChecklistTaskRow,
} from './checklists-task.page.types'
import {
  getChecklistTaskEmptyStateDescription,
  getNestedFormCount,
  getSortedChecklistInputs,
  isChecklistAttachmentRow,
  isChecklistInputTypeForm,
  isChecklistInputTypeVehicleForm,
  isChecklistInstructionsRow,
  isChecklistSignaturesRow,
} from './checklists-task.page.utils'
import { RequestRevisionPopup } from './request-revision-popup/request-revision-popup'

export const ChecklistsTaskPage = () => {
  const { t } = useTranslation(['checklists', 'common'])
  const { taskId } = useParams<{ taskId: string }>()
  const { navigate } = routerFactory.useRouter()

  const { mutateAsync: deleteChecklist } = useDeleteChecklist()
  const { mutateAsync: sendReminderChecklist } = useSendReminderChecklist()
  const { mutateAsync: approveChecklist } = useApproveChecklist()
  const { mutateAsync: updateStatusChecklist, isPending: isStatusUpdatePending } =
    useBulkUpdateStatusChecklists()

  const { data: checklistTaskDetails, isLoading } = useGetChecklistTaskDetails({
    checklistId: taskId,
  })

  const checklistTaskRows: ChecklistTaskRow[] = useMemo(() => {
    // Note: If inputs are not sent by user - don't even show the instructions and attachments rows
    // Just render the empty state
    if (!checklistTaskDetails || checklistTaskDetails.inputs?.length === 0) {
      return []
    }

    const attachmentRow: ChecklistAttachmentRow = {
      id: 'attachment',
      type: 'attachment',
      url: getAssetLink({
        file: checklistTaskDetails?.attachment ?? undefined,
        path: 'chitown-images/checklist-attachments',
      }),
    }

    const instructionsRow: ChecklistInstructionsRow = {
      html: checklistTaskDetails?.instructions,
      id: 'instructions',
      type: 'instructions',
    }

    const signaturesRow: ChecklistSignaturesRow = {
      checklistId: checklistTaskDetails?.id,
      id: 'signatures',
      signatures: checklistTaskDetails?.signatures,
      type: 'signatures',
    }

    const rows = [
      ...(checklistTaskDetails?.attachment ? [attachmentRow] : []),
      ...(checklistTaskDetails?.instructions ? [instructionsRow] : []),
      ...(checklistTaskDetails?.signatures ? [signaturesRow] : []),
      ...getSortedChecklistInputs(checklistTaskDetails.inputs),
    ]

    return rows
  }, [checklistTaskDetails])

  const handleDeleteClick = () => {
    if (!checklistTaskDetails) return

    popupFactory.open(ConfirmationPopup, {
      children: t('checklists:task.delete.confirmBody'),
      confirmText: t('common:actions.delete'),
      onConfirm: async () => {
        await deleteChecklist({
          id: checklistTaskDetails.id,
        })
        navigate({
          path: '/dashboard/building-dashboard/checklists',
        })
      },
      title: t('checklists:task.delete.confirmTitle'),
    })
  }

  const handleSendReminderClick = () => {
    if (!checklistTaskDetails) return

    popupFactory.open(ConfirmationPopup, {
      children: t('checklists:task.sendReminder.confirmBody'),
      confirmText: t('checklists:task.sendReminder.confirmText'),
      onConfirm: async () => {
        await sendReminderChecklist({
          buildingId: checklistTaskDetails.building.id,
          checklistId: checklistTaskDetails.id,
          clientId: checklistTaskDetails.resident.clientId,
        })
      },
      title: t('checklists:task.sendReminder.confirmTitle'),
      variant: 'warning',
    })
  }

  const handleApproveClick = () => {
    if (!checklistTaskDetails) return

    popupFactory.open(ConfirmationPopup, {
      children: t('checklists:task.approve.confirmBody'),
      confirmText: t('checklists:task.approve.confirmText'),
      onConfirm: async () => {
        await approveChecklist({
          checklistId: checklistTaskDetails.id,
        })
      },
      title: t('checklists:task.approve.confirmTitle'),
      variant: 'warning',
    })
  }

  const handleRequestRevisionClick = () => {
    if (!checklistTaskDetails) return

    popupFactory.open(RequestRevisionPopup, {
      checklistId: checklistTaskDetails.id,
    })
  }

  const handleStatusChange = async (status: ChecklistBulkStatus) => {
    if (!checklistTaskDetails) return

    await updateStatusChecklist({
      buildingId: checklistTaskDetails.building.id,
      checklistIds: [checklistTaskDetails.id],
      status,
    })
  }

  const statusMenuItems = checklistTaskDetails
    ? getChecklistBulkStatuses(checklistTaskDetails.status).map((status) => ({
        ...getChecklistBulkStatusAction(status, t),
        onClick: () => handleStatusChange(status),
      }))
    : []

  const hasInputs = checklistTaskRows.length > 0
  const showSendReminderButton =
    checklistTaskDetails?.status === 'not-started' || checklistTaskDetails?.status === 'past-due'
  const showRequestRevisionButton = checklistTaskDetails?.status === 'pending-review'
  const showApproveButton = checklistTaskDetails?.status === 'pending-review'

  return (
    <>
      <PageHeader
        loading={isLoading}
        title={checklistTaskDetails?.name ?? ''}
        actions={
          <>
            <Button onlyBox use="secondary" size="small" onClick={handleDeleteClick}>
              <Icon icon={TrashIcon} />
            </Button>
            {statusMenuItems.length > 0 && (
              <DropdownMenu
                items={statusMenuItems}
                trigger={({ isOpen }) => (
                  <Button use="secondary" size="small" loading={isStatusUpdatePending}>
                    {t('checklists:task.changeStatusButton')}
                    <S.CaretIcon icon={CaretUpIcon} $isOpen={isOpen} />
                  </Button>
                )}
              />
            )}
            {showSendReminderButton && (
              <Button use="secondary" size="small" onClick={handleSendReminderClick}>
                <Icon icon={BellRingingIcon} />
                {t('checklists:task.sendReminder.label')}
              </Button>
            )}
            {showRequestRevisionButton && (
              <Button use="secondary" size="small" onClick={handleRequestRevisionClick}>
                <Icon icon={EyeIcon} />
                {t('checklists:task.requestRevision.label')}
              </Button>
            )}
            {showApproveButton && (
              <Button size="small" onClick={handleApproveClick}>
                <Icon icon={CheckIcon} />
                {t('checklists:task.approve.label')}
              </Button>
            )}
          </>
        }
        backButton={
          <Link
            navigateBack
            use="secondary"
            size="small"
            to={{ path: '/dashboard/building-dashboard/checklists' }}
          >
            <Icon icon={ArrowLeftIcon} />
            {t('common:actions.back')}
          </Link>
        }
        badges={
          checklistTaskDetails && (
            <>
              <Badge
                color={getChecklistStatusColor(checklistTaskDetails.status)}
                variant="light"
                size="small"
              >
                {getChecklistStatusLabel(checklistTaskDetails.status, t)}
              </Badge>
              <Badge
                color={getChecklistTypeColor(checklistTaskDetails.type)}
                variant="light"
                size="small"
              >
                {getChecklistTypeLabel(checklistTaskDetails.type, t)}
              </Badge>
            </>
          )
        }
      />
      <S.TaskDetailsCard loading={isLoading}>
        <InfoRow
          grayTitle
          icon={ClockCountdownIcon}
          title={t('checklists:task.info.dueDate')}
          subtitle={formatDateLong(checklistTaskDetails?.dueDate)}
        />
        <InfoRow
          grayTitle
          icon={ClockCountdownIcon}
          title={t('checklists:task.info.completedDate')}
          subtitle={formatDateOnly(checklistTaskDetails?.completedAt)}
        />
        <InfoRow
          grayTitle
          icon={UserSoundIcon}
          title={t('checklists:task.info.resident')}
          subtitle={`${checklistTaskDetails?.resident.firstName} ${checklistTaskDetails?.resident.lastName}`}
        />
        {/* TODO: Add proper phone number formatting when data will arrive in new format with country code */}
        <InfoRow
          grayTitle
          icon={PhoneIcon}
          title={t('checklists:task.info.phone')}
          subtitle={checklistTaskDetails?.resident.phone}
        />
      </S.TaskDetailsCard>
      {hasInputs && !isLoading && (
        <Card>
          <Table
            id="checklists-task"
            loading={isLoading}
            columns={getChecklistTaskColumns(t)}
            data={checklistTaskRows}
            getRowKey={(row) => row.id}
            renderCollapsableRow={(row) => {
              if (
                isChecklistAttachmentRow(row) ||
                isChecklistInstructionsRow(row) ||
                isChecklistSignaturesRow(row)
              ) {
                return null
              }

              // Note: For form input we render extra expandable row with form details
              // But don't show the extra row if answers are not sent by user yet
              if (isChecklistInputTypeForm(row) && getNestedFormCount(row) > 0) {
                return <ChecklistInputFormRow input={row} />
              }

              if (isChecklistInputTypeVehicleForm(row) && getNestedFormCount(row) > 0) {
                return <ChecklistInputFormRow input={row} />
              }

              return null
            }}
          />
        </Card>
      )}
      {!hasInputs && !isLoading && checklistTaskDetails && (
        <S.EmptyStateContainer>
          <EmptyState
            title={t('checklists:task.emptyState.title')}
            description={getChecklistTaskEmptyStateDescription(checklistTaskDetails.status, t)}
          />
        </S.EmptyStateContainer>
      )}
    </>
  )
}
